import { format, parseISO, parse } from 'date-fns';
import { utcToZonedTime, formatInTimeZone } from 'date-fns-tz';

// New common method for date formatting
function formatDate(dateString, formatString = 'MM/dd/yyyy') {
  try {
    // Check if the date string is in UTC format (contains 'T' and 'Z')
    const isUTCFormat = dateString.includes('T') && dateString.includes('Z');

    if (formatString === "MM/dd/yyyy") {
      return format(parseISO(dateString), formatString);
    } else {
      // For other formats, handle time zones only if it's UTC format
      const date = parseISO(dateString);
      const timeZone = 'America/Phoenix';

      if (isUTCFormat) {
        const zonedDate = utcToZonedTime(date, timeZone);
        return format(zonedDate, formatString, { timeZone });
      } else {
        // For local time format, use the date directly
        return format(date, formatString);
      }
    }
  } catch (error) {
    // Return a default value on error
    return '-';
  }
}

// DateFormat: A component used for formatting dates within JSX.
// It takes a 'dateString' and an optional 'formatString', then returns the formatted date as a JSX element.
export function DateFormat({ dateString, formatString = 'MM/dd/yyyy' }) {
  return formatDate(dateString, formatString);
}

export function TimeFormat({ timeString, formatString = 'hh:mm:ss a' }) {
  if (!timeString) {
    return '-';
  }
  try {
    const parsedTime = parse(timeString, 'HH:mm:ss', new Date());
    return format(parsedTime, formatString);
  } catch (error) {
    return timeString;
  }
}

// Input: 2024-03-06T15:13:01.000Z
// Output: 03/07/2024
export function FormatISODate(isoString) {
  try {
    return format(parseISO(isoString), 'MM/dd/yyyy');
  } catch (error) {
    return '-';
  }
}

// Input: 2024-03-06T16:34:10.000000Z
// Output: 03/06/2024 09:34:10 AM (In America/Phoenix time zone)
export function FormatISODateTime(isoString) {
  try {
    return formatInTimeZone(isoString, 'America/Phoenix', 'MM/dd/yyyy hh:mm:ss a');
  } catch (error) {
    return '-';
  }
}

// Input: 2024-03-06T16:34:10.000000Z
// Output: 2024-03-06 09:34 (In America/Phoenix time zone)
export function FormatISODateTime24HoursDB(isoString) {
  try {
    return formatInTimeZone(isoString, 'America/Phoenix', 'yyyy-MM-dd HH:mm');
  } catch (error) {
    return '-';
  }
}

// Input: 2024-03-07T16:05:36.000000Z
// Output: 03/07/2024
export function FormatISOToAZDate(isoString, formatString = 'MM/dd/yyyy') {
  try {
    return formatInTimeZone(isoString, 'America/Phoenix', formatString);
  } catch (error) {
    return '-';
  }
}

// SPECIAL CASE
// backend is not returning with timezone, so its returning as arizona time as default
// Keep that as it is
export function FormatDateTimeToSame(isoString, formatString = 'MM/dd/yyyy') {
  try {
    return format(parseISO(isoString), formatString);
  } catch (error) {
    return '-';
  }
}

export function CurrencyFormat({ value, showCurrencySign = true, showCents = true }) {
  try {
    if (value == undefined || Number.isNaN(value)) return '-';
    if (Object.is(value, -0.00)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: showCurrencySign ? 'currency' : 'decimal',
      currency: 'USD',
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0
    }).format(value);
  } catch (error) {
    return '-';
  }
}

export function PercentFormat({ value, percentSign = true, fractionDigits = 2 }) {
  try {
    if (value == undefined || Number.isNaN(value)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true
    }).format(value) + (percentSign ? '%' : '');
  } catch (error) {
    return '-';
  }
}

export function formatApr(value) {
  if (value == undefined || value === null || value === '') return '0';
  // If it's a string, preserve it as-is (preserves trailing zeros)
  if (typeof value === 'string') {
    return value;
  }
  // If it's a number, convert to string (won't have trailing zeros)
  return String(value);
}

export function getOrdinalSuffix(number) {
  let j = number % 10;
  let k = number % 100;
  if (j === 1 && k !== 11) {
    return number + "st";
  }
  if (j === 2 && k !== 12) {
    return number + "nd";
  }
  if (j === 3 && k !== 13) {
    return number + "rd";
  }
  return number + "th";
}

export function appendStringWithValue(str, value) {
  if (value != 0) {
    return str + ": " + CurrencyFormat({ value: value });
  }
  return "";
}

export function isDateAfterDays(date, days) {
  const today = new Date(new Date().setDate(new Date().getDate() - days));
  const newDate = today.toISOString().split('T')[0];

  return date > newDate;
}

export function buildQueryString(obj) {
  let queryString = Object.keys(obj)
    .filter((key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== '')
    .map((key) => key + '=' + encodeURIComponent(obj[key]))
    .join('&');
  if (queryString !== '') {
    return '?' + queryString;
  }
  return queryString;
}

// Convert newlines to <br> tags if the body is plain text and no HTML tags are present
export function nlToBrIfPlainText(body) {
  // If there's already html, leave it alone
  if (/<\/?[a-z][\s\S]*?>/i.test(body)) {
    return body; // If there's HTML, leave it as-is.
  }

  // Convert newlines to <br> tags if any exist
  if (body.indexOf('\n') !== -1) {
    return body.replace(/\n/g, '<br>');
  }

  return body;
}

// Search a blob of text and replace any times in HH:mm:ss format with the same time in 12-hour format
function convertDateTimesToTimeOnly(text) {
  const dateTimeRegex = /(\d{2}:\d{2}:\d{2})/g;

  return text.replace(dateTimeRegex, (match) => {
    try {
      // Parse the matched date string using the expected format.
      const parsedDate = parse(match, 'HH:mm:ss', new Date());
      // Format the parsed date to show only the time in 12-hour format.
      return format(parsedDate, 'hh:mm a');
    } catch (error) {
      // If parsing fails, return the original match.
      return match;
    }
  });
}

// For chats recorded in notes, add "Chat started" and "Chat ended" messages if not present
function addChatStartAndEnd(text) {
  // If the text already contains "Chat started" or "Chat ended", return it as is.
  if (text.includes('Chat started') && text.includes('Chat ended')) {
    return text;
  }

  // Split the text into individual lines.
  const lines = text.split('<br>');
  // Regex to match lines that contain only a time in HH:mm:ss format.
  const timeRegex = /^\d{2}:\d{2}:\d{2}$/;

  // Filter out the lines that match our time format.
  const times = lines.filter((line) => timeRegex.test(line.trim()));

  // If no valid time lines are found, return the original text.
  if (times.length === 0) {
    return text;
  }

  // Use the first and last time lines.
  const startTime = times[0];
  const endTime = times[times.length - 1];

  // Parse the time strings (using any default date since we're only interested in the time).
  const parsedStart = parse(startTime, 'HH:mm:ss', new Date());
  const parsedEnd = parse(endTime, 'HH:mm:ss', new Date());

  // Format the times to a 12-hour format with AM/PM.
  const formattedStart = format(parsedStart, 'h:mm a');
  const formattedEnd = format(parsedEnd, 'h:mm a');

  if (!text.includes('Chat started')) {
    text = `Chat started ${formattedStart}<br><br>${text}`
  }
  if (!text.includes('Chat ended')) {
    text = `${text}<br><br>Chat ended ${formattedEnd}`;
  }

  return text;
}

function collapseMultipleEmptyLines(text) {
  // If there's no <br> tags, return the text as is.
  if (!text.includes('<br>')) {
    return text;
  }
  return text.replace(/<br>(<br>)+/g, '<br><br>');
}

export function formatNoteBody(note) {
  let formattedBody = note?.body;
  formattedBody = nlToBrIfPlainText(note?.body);
  // Check if the note is of a certain type
  if (note?.activity_code_tag_id === 395) {
    formattedBody = addChatStartAndEnd(formattedBody);
    formattedBody = convertDateTimesToTimeOnly(formattedBody);
  }
  formattedBody = collapseMultipleEmptyLines(formattedBody);
  return formattedBody;
}

// Always get today's date for Arizona, regardless of where the code runs.
export function getTodayArizona(fmt = 'yyyy-MM-dd') {
  return formatInTimeZone(new Date(), 'America/Phoenix', fmt);
}

// Input: 2024-03-06T16:34:10.000000Z
// Output: 09:34:10 AM (In America/Phoenix time zone)
export function FormatISOTime(isoString) {
  if (!isoString) return '';
  try {
    return formatInTimeZone(isoString, 'America/Phoenix', 'hh:mm:ss a');
  } catch (error) {
    return '-';
  }
}

// Input: seconds (number)
// Output: "125s (00:02:05)"
export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Math.round(Math.max(0, seconds));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${s}s (${h}:${m}:${ss})`;
}
